import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler } from '@app/store/actions';
import { FormControl } from '@material-ui/core';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { genericErrorString } from '@app/helpers';
import { scheduledJobsService } from '@app/services';
import { AxiosError } from 'axios';
import { IScheduledJobs } from '@app/models';

const validator = require('validator');

export interface IReceivedData {
    dialogOpen: Function;
    jobDetails: IScheduledJobs;
    jobId: string;
    jobScheduler: string;
}

export const TriggerJobDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [tenantId, setTenantId] = useState<string>('');
    const [organizationId, setOrganizationId] = useState<string>('');
    const [showJobOptions, setShowJobOptions] = useState<boolean>(false);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        switch (props.jobDetails.JobName) {
            case 'Office365Provisioning':
            case 'OrphanWorker':
                setShowJobOptions(true);
                break;
            default:
                setShowJobOptions(false);
                break;
        }
    }, []);

    const handleSaveBtn = () => {
        let jsonObject: any = {};
        jsonObject.options = {};
        jsonObject.options.JobGroup = props.jobDetails.JobGroup;
        jsonObject.options.JobName = props.jobId;
        jsonObject.options.Scheduler = props.jobScheduler;

        if (tenantId !== '') {
            // make sure tenant id is guid format
            const valid: boolean = validator.isUUID(tenantId);

            if (!valid) {
                setErrorMessage(translation('TENANT_ID_GUID'));
                return;
            }

            jsonObject.options.TenantId = tenantId;
        }

        if (organizationId !== '') {
            // make sure org id is guid format
            const valid: boolean = validator.isUUID(organizationId);

            if (!valid) {
                setErrorMessage(translation('ORGANIZATION_ID_GUID'));
                return;
            }

            jsonObject.options.OrganizationId = organizationId;
        }

        scheduledJobsService.triggerJob(jsonObject, handleSuccess, handleError);
    };

    const handleError = (response: AxiosError<any>) => {
        const error: string = genericErrorString(response);

        setErrorMessage(error);
    };

    const onTenantIdChange = (e: any) => {
        const { name, value } = e.target;

        setTenantId(value);
    };

    const onOrganizationIdChange = (e: any) => {
        const { name, value } = e.target;

        setOrganizationId(value);
    };

    const handleSuccess = () => {
        props.dialogOpen(true);
    };

    const handleCancel = () => {
        props.dialogOpen(false);
    };

    return (
        <>
            <form method='post' className='modal__form modal__medium' tabIndex={0} onKeyPress={(e: React.KeyboardEvent<HTMLFormElement>) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveBtn();
                }
            }}>
                <div className='modal__body--row'>
                    {translation('JOB_QUEUE_TRIGGER_DESC')}
                </div>
                {showJobOptions &&
                    <>
                        <div className='modal__body--row'>
                            <FormControl>
                                <PasslyTextField
                                    name='tenantId'
                                    label={'TENANT_ID'}
                                    value={tenantId}
                                    onChange={onTenantIdChange}
                                    id='tenantId-input'
                                    autoFocus
                                />
                            </FormControl>
                        </div>
                        <div className='modal__body--row'>
                            <FormControl>
                                <PasslyTextField
                                    name='organizationId'
                                    label={'AUDIT_LOG_NAME_OID'}
                                    value={organizationId}
                                    onChange={onOrganizationIdChange}
                                    id='organizationId-input'
                                />
                            </FormControl>
                        </div>
                    </>
                }
            </form>

            {errorMessage !== undefined && errorMessage !== '' && <div className='modal__error'>{errorMessage}</div>}

            <ModalActionsComponent
                primaryBtn={translation('TRIGGER_JOB')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                id='triggerJob'
            />
        </>
    );
};
